/* eslint-disable import/no-anonymous-default-export */
import { AxiosError } from "axios";
import useApi, { ActionResult, Query } from "../../common/hooks/useApi";

// NOTA DE CASING: a pesar de lo que dice HANDOFF-notificaciones-email.md, las entidades
// OData reales (LoteImpresion, Infraccion) devuelven el body en PascalCase (Id, FechaHora)
// y los enums como string (Estado: "PENDIENTE", no 4) — verificado contra /odata/v1/LoteImpresion
// e /odata/v1/Infraccion con datos reales. Coincide con el resto de la app (useLotesImpresionAPI.tsx).
// Las acciones custom (Notificacion/email/lote, .../estado, .../grupo/estado, .../reanudar) no son
// entidades OData sino POCOs propios — no se pudieron probar en vivo sin disparar un envío real, así
// que su parseo tolera camelCase (como documenta el handoff) o PascalCase (como todo lo demás).
// $filter/$select/$orderby/$expand siempre van en PascalCase (nombre del modelo C#), eso sí
// coincide con el handoff.

export type MedioNotificacion = "EMAIL" | "POSTAL" | "WHATSAPP";

// Query string (?tipo=...) acepta el nombre. En las respuestas puede venir el nombre (el DTO OData de
// LoteNotificacion lo serializa como "RECORDATORIO_PT") o el número (0 PRODUCCION, 1 CAMPANIA,
// 2 RECORDATORIO_PV, 3 RECORDATORIO_PT, 4 PRODUCCION_VENCIDA), así que tipoLoteLabel acepta ambos.
export type TipoLoteNotificacion = "PRODUCCION" | "CAMPANIA" | "RECORDATORIO_PV" | "RECORDATORIO_PT" | "PRODUCCION_VENCIDA";
const TIPO_LABELS: Record<number, TipoLoteNotificacion> = { 0: "PRODUCCION", 1: "CAMPANIA", 2: "RECORDATORIO_PV", 3: "RECORDATORIO_PT", 4: "PRODUCCION_VENCIDA" };
export const tipoLoteLabel = (tipo: number | string): TipoLoteNotificacion => {
    if (typeof tipo === "string") {
        const nombre = tipo.toUpperCase();
        if (nombre in TIPO_LOTE_DISPLAY) return nombre as TipoLoteNotificacion;
        const n = Number(tipo);
        return (Number.isInteger(n) && TIPO_LABELS[n]) || "PRODUCCION";
    }
    return TIPO_LABELS[tipo] ?? "PRODUCCION";
};
// Texto y color del Tag con que se muestra cada tipo en historial / estado de lote.
export const TIPO_LOTE_DISPLAY: Record<TipoLoteNotificacion, { texto: string, color: string }> = {
    PRODUCCION: { texto: "PRODUCCIÓN", color: "default" },
    CAMPANIA: { texto: "CAMPAÑA", color: "purple" },
    RECORDATORIO_PV: { texto: "RECORDATORIO PAGO VOLUNTARIO", color: "orange" },
    RECORDATORIO_PT: { texto: "RECORDATORIO PAGO TOTAL", color: "cyan" },
    PRODUCCION_VENCIDA: { texto: "PRODUCCIÓN VENCIDA", color: "volcano" },
};

// Prefijo con que el backend marca en LoteNotificacionDetalle.Error las actas que quedaron afuera a
// propósito (sin email, en convenio, ya no vencida). Vienen con Enviado = false pero no son fallas.
export const PREFIJO_DESCARTADA = "Descartada: ";
export const esDescartada = (error?: string | null): boolean => !!error && error.startsWith(PREFIJO_DESCARTADA);

// GET Notificacion/email/lote-impresion/vencidos: lotes de impresión con actas vencidas (2º vencimiento
// pasado, impagas, sin juzgar) para el envío PRODUCCION_VENCIDA.
export interface LoteImpresionVencidoOption {
    loteImpresionId: number
    nroLote: number
    fechaHora: string
    unidadesProdNombre?: string
    cantidadInfracciones: number
    cantidadInfraccionesExcluidas?: number
    cantidadVencidas: number
    cantidadVencidasGeneradas: number
}

export interface LoteImpresionOption {
    Id: number
    FechaHora: string
    NroLote: number
    CantidadInfracciones: number
    CantidadInfraccionesExcluidas?: number
    UnidadesProdNombre?: string
}

export interface ConfiguracionProduccionOption {
    Id: number
    Nombre: string
    Subtitulo?: string
    UnidadProduccion?: { Nombre: string }
}

// GET Notificacion/email/preview: reemplaza al viejo preview contra /odata/v1/Infraccion —
// este endpoint es el único que aplica también la condición fija de "tiene email" del backend
// (sección 2.2/4 del handoff), así que el preview queda sincronizado con el envío real.
export interface PreviewActa {
    infraccionId: number
    nroTramite: string
    fechaHora?: string
    patente?: string
    fechaVencimiento?: string
    fecha2doVencimiento?: string
    actaEnviadaEmail: boolean
    fechaActaEnviadaEmail?: string
    titularNombreCompleto?: string
    titularProvincia?: string
    titularCodigoPostal?: string
    titularEmails?: string
}

export interface PreviewEnvioResponse {
    cantidadTotal: number
    cantidadConEmail: number
    cantidadSinEmail: number
    // Solo cuando se excluyen convenios: actas que matchean pero quedan afuera por estar en un convenio.
    cantidadEnConvenio: number
    actas: PreviewActa[]
}

// GET /LoteNotificacion (listado genérico OData): no trae exitosas/fallidas/pendientes/activo,
// eso solo lo da el endpoint de estado (2.4 del handoff) por lote.
//
// LoteImpresionId/LoteImpresion: todavía NO los devuelve el backend (no hay forma de saber desde
// qué lote de impresión se generó un envío ya hecho). Quedan como opcionales a propósito: el día
// que el backend agregue el campo/la navegación, esto empieza a mostrarse solo, sin tocar nada
// más acá. Ver handoff (sección "Pendiente: lote de impresión de origen").
export interface LoteNotificacionResumen {
    Id: number
    FechaHora: string
    Medio: MedioNotificacion
    Tipo: number | TipoLoteNotificacion
    CantidadActas: number
    UsuarioGenerador?: { UserName: string }
    LoteImpresionId?: number
    LoteImpresion?: { NroLote: number }
}

// null = pendiente (no procesada todavía), true = enviada (y si era campaña, regenerada con éxito),
// false = falló (ver Error, puede ser fallo de regeneración o de envío)
export interface LoteNotificacionDetalle {
    Id: number
    LoteNotificacionId: number
    InfraccionId: number
    NroTramite: string
    Enviado: boolean | null
    FechaEnvio?: string
    Error?: string
}

// Campañas de más de 1000 actas se parten en tandas encadenadas; producción y campañas chicas
// siempre tienen un solo elemento en `lotes` y loteNotificacionGrupoId viene null.
export interface LoteEnGrupo {
    loteNotificacionId: number
    ordenEnGrupo: number
    cantidadActas: number
}

export interface ActaExcluidaPorEnCurso {
    infraccionId: number
    nroTramite: string
    loteNotificacionIdEnCurso: number
}

export interface EnviarLoteResponse {
    cantidadActasTotal: number
    loteNotificacionGrupoId: number | null
    lotes: LoteEnGrupo[]
    actasExcluidasPorEnCurso: ActaExcluidaPorEnCurso[]
}

export interface EstadoLoteNotificacion {
    loteNotificacionId: number
    loteNotificacionGrupoId: number | null
    ordenEnGrupo: number
    tipo: number | TipoLoteNotificacion
    cantidadActas: number
    cantidadExitosas: number
    cantidadFallidas: number
    // Quedaron afuera a propósito (ver PREFIJO_DESCARTADA): no cuentan como fallidas.
    cantidadDescartadas: number
    cantidadPendientes: number
    completado: boolean
    activo: boolean
    // Igual que en LoteNotificacionResumen: el backend todavía no lo devuelve (solo aplica a
    // tipo PRODUCCION, una campaña no viene de un lote de impresión puntual). Opcional a propósito.
    loteImpresionId?: number
    loteImpresionNroLote?: number
}

// Estado agregado (suma de todas las tandas) + detalle de cada tanda individual.
export interface TandaEnGrupo {
    loteNotificacionId: number
    ordenEnGrupo: number
    cantidadActas: number
    cantidadExitosas: number
    cantidadFallidas: number
    cantidadDescartadas: number
    cantidadPendientes: number
    completado: boolean
    activo: boolean
}

export interface EstadoGrupoNotificacion {
    loteNotificacionGrupoId: number
    tipo: number | TipoLoteNotificacion
    loteImpresionId?: number
    cantidadActas: number
    cantidadExitosas: number
    cantidadFallidas: number
    cantidadDescartadas: number
    cantidadPendientes: number
    completado: boolean
    activo: boolean
    lotes: TandaEnGrupo[]
}

export interface ReanudarLoteResult {
    loteNotificacionId: number
    cantidadPendientes: number
}

// Tab "Resumen" (generar lote de notificación): a pedido, el resumen se calcula por LOTE DE
// IMPRESIÓN (no por LoteNotificacion), porque un mismo lote de impresión puede tener varios
// envíos/reintentos de notificación asociados. Este endpoint ES NUEVO y todavía no existe en el
// backend — hay que implementarlo. Contrato esperado:
//
//   GET Notificacion/email/lote-impresion/resumen
//       ?fechaEnvioDesde=<ISO>&fechaEnvioHasta=<ISO>&configuracionProduccionId=<n, opcional>
//
// El backend busca los envíos de notificación (LoteNotificacion/LoteNotificacionDetalle, o las
// actas de Infraccion con ActaEnviadaEmail) cuya fecha de envío cae dentro del rango, filtra
// opcionalmente por configuración de producción (del equipo que generó el lote de impresión), y
// agrupa el resultado por lote de impresión: una fila por lote de impresión con el total de actas
// que tiene, cuántas se enviaron por email y cuántas no, dentro del rango filtrado.
//
// Response: array de objetos (PascalCase, como el resto de las entidades OData reales — ver nota
// de casing arriba; se lee con `pick` para tolerar camelCase igual que las demás acciones custom):
//   { LoteImpresionId, NroLote, FechaEnvio, ConfiguracionProduccionId, ConfiguracionProduccionNombre,
//     CantidadActasTotal, CantidadEnviadas, CantidadNoEnviadas }
export interface ResumenLoteImpresion {
    loteImpresionId: number
    nroLote: number
    fechaEnvio?: string
    configuracionProduccionId?: number
    configuracionProduccionNombre?: string
    cantidadActasTotal: number
    cantidadEnviadas: number
    cantidadNoEnviadas: number
}

export interface ResumenLotesFiltro {
    fechaEnvioDesde?: string
    fechaEnvioHasta?: string
    configuracionProduccionId?: number
}

// Las acciones custom (no-entidad) no se pudieron probar en vivo sin disparar un envío real de
// emails, así que leemos ambos casings posibles en vez de asumir uno.
const pick = <T,>(body: any, camelKey: string, pascalKey: string, fallback: T): T =>
    body?.[camelKey] ?? body?.[pascalKey] ?? fallback;

const mapLoteEnGrupo = (item: any): LoteEnGrupo => ({
    loteNotificacionId: pick(item, "loteNotificacionId", "LoteNotificacionId", 0),
    ordenEnGrupo: pick(item, "ordenEnGrupo", "OrdenEnGrupo", 1),
    cantidadActas: pick(item, "cantidadActas", "CantidadActas", 0),
});

const mapActaExcluida = (item: any): ActaExcluidaPorEnCurso => ({
    infraccionId: pick(item, "infraccionId", "InfraccionId", 0),
    nroTramite: pick(item, "nroTramite", "NroTramite", ""),
    loteNotificacionIdEnCurso: pick(item, "loteNotificacionIdEnCurso", "LoteNotificacionIdEnCurso", 0),
});

const mapPreviewActa = (item: any): PreviewActa => ({
    infraccionId: pick(item, "infraccionId", "InfraccionId", 0),
    nroTramite: pick(item, "nroTramite", "NroTramite", ""),
    fechaHora: pick(item, "fechaHora", "FechaHora", undefined),
    patente: pick(item, "patente", "Patente", undefined),
    fechaVencimiento: pick(item, "fechaVencimiento", "FechaVencimiento", undefined),
    fecha2doVencimiento: pick(item, "fecha2doVencimiento", "Fecha2doVencimiento", undefined),
    actaEnviadaEmail: pick(item, "actaEnviadaEmail", "ActaEnviadaEmail", false),
    fechaActaEnviadaEmail: pick(item, "fechaActaEnviadaEmail", "FechaActaEnviadaEmail", undefined),
    titularNombreCompleto: pick(item, "titularNombreCompleto", "TitularNombreCompleto", undefined),
    titularProvincia: pick(item, "titularProvincia", "TitularProvincia", undefined),
    titularCodigoPostal: pick(item, "titularCodigoPostal", "TitularCodigoPostal", undefined),
    titularEmails: pick(item, "titularEmails", "TitularEmails", undefined),
});

const mapResumenLoteImpresion = (item: any): ResumenLoteImpresion => ({
    loteImpresionId: pick(item, "loteImpresionId", "LoteImpresionId", 0),
    nroLote: pick(item, "nroLote", "NroLote", 0),
    fechaEnvio: pick(item, "fechaEnvio", "FechaEnvio", undefined),
    configuracionProduccionId: pick(item, "configuracionProduccionId", "ConfiguracionProduccionId", undefined),
    configuracionProduccionNombre: pick(item, "configuracionProduccionNombre", "ConfiguracionProduccionNombre", undefined),
    cantidadActasTotal: pick(item, "cantidadActasTotal", "CantidadActasTotal", 0),
    cantidadEnviadas: pick(item, "cantidadEnviadas", "CantidadEnviadas", 0),
    cantidadNoEnviadas: pick(item, "cantidadNoEnviadas", "CantidadNoEnviadas", 0),
});

const mapTandaEnGrupo = (item: any): TandaEnGrupo => ({
    loteNotificacionId: pick(item, "loteNotificacionId", "LoteNotificacionId", 0),
    ordenEnGrupo: pick(item, "ordenEnGrupo", "OrdenEnGrupo", 1),
    cantidadActas: pick(item, "cantidadActas", "CantidadActas", 0),
    cantidadExitosas: pick(item, "cantidadExitosas", "CantidadExitosas", 0),
    cantidadFallidas: pick(item, "cantidadFallidas", "CantidadFallidas", 0),
    cantidadDescartadas: pick(item, "cantidadDescartadas", "CantidadDescartadas", 0),
    cantidadPendientes: pick(item, "cantidadPendientes", "CantidadPendientes", 0),
    completado: pick(item, "completado", "Completado", false),
    activo: pick(item, "activo", "Activo", false),
});

const mapLoteImpresionVencido = (item: any): LoteImpresionVencidoOption => ({
    loteImpresionId: pick(item, "loteImpresionId", "LoteImpresionId", 0),
    nroLote: pick(item, "nroLote", "NroLote", 0),
    fechaHora: pick(item, "fechaHora", "FechaHora", ""),
    unidadesProdNombre: pick(item, "unidadesProdNombre", "UnidadesProdNombre", undefined),
    cantidadInfracciones: pick(item, "cantidadInfracciones", "CantidadInfracciones", 0),
    cantidadInfraccionesExcluidas: pick(item, "cantidadInfraccionesExcluidas", "CantidadInfraccionesExcluidas", undefined),
    cantidadVencidas: pick(item, "cantidadVencidas", "CantidadVencidas", 0),
    cantidadVencidasGeneradas: pick(item, "cantidadVencidasGeneradas", "CantidadVencidasGeneradas", 0),
});

const buildLotesImpresionSearchFilter = (search?: string): string[] => {
    const trimmed = search?.trim();
    if (!trimmed) return [];
    const clauses = [`contains(UnidadesProdNombre,'${trimmed}')`];
    if (/^\d+$/.test(trimmed)) clauses.push(`NroLote eq ${trimmed}`);
    return [clauses.length > 1 ? `(${clauses.join(" or ")})` : clauses[0]];
};

export interface EnviarLoteOpciones {
    // Solo CAMPANIA: PRODUCCION_VENCIDA siempre usa los días default y recalcula valores.
    diasVencimiento?: number
    mantenerValoresRegeneracion?: boolean
    // CAMPANIA lo deja opcional; PRODUCCION_VENCIDA siempre excluye convenios (lo fuerza el backend).
    excluirConvenios?: boolean
    // PRODUCCION / PRODUCCION_VENCIDA: lote de impresión de origen (queda guardado en el historial).
    loteImpresionId?: number
}

export interface PreviewOpciones {
    tipo?: TipoLoteNotificacion
    excluirConvenios?: boolean
}

export default () => {
    const loteImpresionApi = useApi({ module: "LoteImpresion" });
    const configuracionProduccionApi = useApi({ module: "ConfiguracionProduccion" });
    const notificacionApi = useApi({ module: "Notificacion" });
    const loteNotificacionApi = useApi({ module: "LoteNotificacion" });
    const loteNotificacionDetalleApi = useApi({ module: "LoteNotificacionDetalle" });

    return {
        // Sin `search`: los 50 lotes de impresión más recientes. Con `search`: matchea por
        // número de lote exacto (si el texto es numérico) o por nombre de unidad de producción
        // (contains) — mismos filtros que ya usa /home/edu/Desarrollo/Neuralsys.client/src/lotes-impresion/LotesImpresionList.tsx
        // sobre la misma entidad, así que se sabe que el backend los soporta.
        listarLotesImpresion: (search?: string): Promise<ActionResult<LoteImpresionOption[]>> =>
            loteImpresionApi.list<LoteImpresionOption>({
                $top: 50,
                $orderby: ["Id desc"],
                $select: ["Id", "FechaHora", "NroLote", "CantidadInfracciones", "CantidadInfraccionesExcluidas", "UnidadesProdNombre"],
                $filter: buildLotesImpresionSearchFilter(search),
            }),

        listarConfiguracionesProduccion: (): Promise<ActionResult<ConfiguracionProduccionOption[]>> =>
            configuracionProduccionApi.list<ConfiguracionProduccionOption>({
                $top: 200,
                $orderby: ["Nombre asc"],
                $select: ["Id", "Nombre", "Subtitulo"],
                $expand: ["UnidadProduccion($select=Nombre)"],
            }),

        // Lotes de impresión con actas vencidas, para el envío PRODUCCION_VENCIDA. `search`: número de
        // lote exacto o parte del nombre de la unidad de producción.
        listarLotesImpresionVencidos: async (search?: string): Promise<ActionResult<LoteImpresionVencidoOption[]>> => {
            try {
                const params = new URLSearchParams();
                if (search?.trim()) params.set("busqueda", search.trim());
                const response = await notificacionApi.axiosInstance.get<any>(`Notificacion/email/lote-impresion/vencidos?${params.toString()}`);
                const body = response.data ?? [];
                const items: any[] = Array.isArray(body) ? body : (pick<any[]>(body, "value", "Value", []) ?? []);
                return { data: { value: items.map(mapLoteImpresionVencido) } };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },

        previsualizarEnvio: async (filter: string[], top = 20, skip = 0, opciones?: PreviewOpciones): Promise<ActionResult<PreviewEnvioResponse>> => {
            try {
                let qs = `$filter=${filter.join(" and ")}&$top=${top}&$skip=${skip}`;
                if (opciones?.tipo) qs += `&tipo=${opciones.tipo}`;
                if (opciones?.excluirConvenios) qs += `&excluirConvenios=true`;
                const response = await notificacionApi.axiosInstance.get<any>(`Notificacion/email/preview?${qs}`);
                const body = response.data ?? {};
                return {
                    data: {
                        value: {
                            cantidadTotal: pick(body, "cantidadTotal", "CantidadTotal", 0),
                            cantidadConEmail: pick(body, "cantidadConEmail", "CantidadConEmail", 0),
                            cantidadSinEmail: pick(body, "cantidadSinEmail", "CantidadSinEmail", 0),
                            cantidadEnConvenio: pick(body, "cantidadEnConvenio", "CantidadEnConvenio", 0),
                            actas: (pick<any[]>(body, "actas", "Actas", []) ?? []).map(mapPreviewActa),
                        },
                    },
                };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },

        enviarLoteEmail: async (
            filter: string[],
            tipo: TipoLoteNotificacion = "PRODUCCION",
            opciones?: EnviarLoteOpciones
        ): Promise<ActionResult<EnviarLoteResponse>> => {
            try {
                let qs = `tipo=${tipo}`;
                if (tipo === "CAMPANIA") {
                    if (opciones?.diasVencimiento != null) qs += `&diasVencimiento=${opciones.diasVencimiento}`;
                    if (opciones?.mantenerValoresRegeneracion != null) qs += `&mantenerValoresRegeneracion=${opciones.mantenerValoresRegeneracion}`;
                    if (opciones?.excluirConvenios) qs += `&excluirConvenios=true`;
                }
                if (opciones?.loteImpresionId != null) qs += `&loteImpresionId=${opciones.loteImpresionId}`;
                qs += `&$filter=${filter.join(" and ")}`;
                const response = await notificacionApi.axiosInstance.post<any>(`Notificacion/email/lote?${qs}`);
                const body = response.data ?? {};
                return {
                    data: {
                        value: {
                            cantidadActasTotal: pick(body, "cantidadActasTotal", "CantidadActasTotal", 0),
                            loteNotificacionGrupoId: pick(body, "loteNotificacionGrupoId", "LoteNotificacionGrupoId", null),
                            lotes: (pick<any[]>(body, "lotes", "Lotes", []) ?? []).map(mapLoteEnGrupo),
                            actasExcluidasPorEnCurso: (pick<any[]>(body, "actasExcluidasPorEnCurso", "ActasExcluidasPorEnCurso", []) ?? []).map(mapActaExcluida),
                        },
                    },
                };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },

        obtenerEstadoLote: async (loteNotificacionId: number): Promise<ActionResult<EstadoLoteNotificacion>> => {
            try {
                const response = await notificacionApi.axiosInstance.get<any>(`Notificacion/email/lote/${loteNotificacionId}/estado`);
                const body = response.data ?? {};
                return {
                    data: {
                        value: {
                            loteNotificacionId: pick(body, "loteNotificacionId", "LoteNotificacionId", loteNotificacionId),
                            loteNotificacionGrupoId: pick(body, "loteNotificacionGrupoId", "LoteNotificacionGrupoId", null),
                            ordenEnGrupo: pick(body, "ordenEnGrupo", "OrdenEnGrupo", 1),
                            tipo: pick(body, "tipo", "Tipo", 0),
                            cantidadActas: pick(body, "cantidadActas", "CantidadActas", 0),
                            cantidadExitosas: pick(body, "cantidadExitosas", "CantidadExitosas", 0),
                            cantidadFallidas: pick(body, "cantidadFallidas", "CantidadFallidas", 0),
                            cantidadDescartadas: pick(body, "cantidadDescartadas", "CantidadDescartadas", 0),
                            cantidadPendientes: pick(body, "cantidadPendientes", "CantidadPendientes", 0),
                            completado: pick(body, "completado", "Completado", false),
                            activo: pick(body, "activo", "Activo", false),
                            loteImpresionId: pick(body, "loteImpresionId", "LoteImpresionId", undefined),
                            loteImpresionNroLote: pick(body, "loteImpresionNroLote", "LoteImpresionNroLote", undefined),
                        },
                    },
                };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },

        obtenerEstadoGrupo: async (loteNotificacionGrupoId: number): Promise<ActionResult<EstadoGrupoNotificacion>> => {
            try {
                const response = await notificacionApi.axiosInstance.get<any>(`Notificacion/email/grupo/${loteNotificacionGrupoId}/estado`);
                const body = response.data ?? {};
                return {
                    data: {
                        value: {
                            loteNotificacionGrupoId: pick(body, "loteNotificacionGrupoId", "LoteNotificacionGrupoId", loteNotificacionGrupoId),
                            tipo: pick(body, "tipo", "Tipo", 1),
                            loteImpresionId: pick(body, "loteImpresionId", "LoteImpresionId", undefined),
                            cantidadActas: pick(body, "cantidadActas", "CantidadActas", 0),
                            cantidadExitosas: pick(body, "cantidadExitosas", "CantidadExitosas", 0),
                            cantidadFallidas: pick(body, "cantidadFallidas", "CantidadFallidas", 0),
                            cantidadDescartadas: pick(body, "cantidadDescartadas", "CantidadDescartadas", 0),
                            cantidadPendientes: pick(body, "cantidadPendientes", "CantidadPendientes", 0),
                            completado: pick(body, "completado", "Completado", false),
                            activo: pick(body, "activo", "Activo", false),
                            lotes: (pick<any[]>(body, "lotes", "Lotes", []) ?? []).map(mapTandaEnGrupo),
                        },
                    },
                };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },

        reanudarLote: async (loteNotificacionId: number): Promise<ActionResult<ReanudarLoteResult>> => {
            try {
                const response = await notificacionApi.axiosInstance.post<any>(`Notificacion/email/lote/${loteNotificacionId}/reanudar`);
                const body = response.data ?? {};
                return {
                    data: {
                        value: {
                            loteNotificacionId: pick(body, "loteNotificacionId", "LoteNotificacionId", loteNotificacionId),
                            cantidadPendientes: pick(body, "cantidadPendientes", "CantidadPendientes", 0),
                        },
                    },
                };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },

        listarHistorialLotes: (query?: Query): Promise<ActionResult<LoteNotificacionResumen[]>> =>
            loteNotificacionApi.list<LoteNotificacionResumen>({
                $top: 50,
                $orderby: ["Id desc"],
                $expand: ["UsuarioGenerador($select=UserName)"],
                ...(query ?? {}),
            }),

        listarDetalleLote: (loteNotificacionId: number, query?: Query): Promise<ActionResult<LoteNotificacionDetalle[]>> =>
            loteNotificacionDetalleApi.list<LoteNotificacionDetalle>({
                $top: 200,
                $orderby: ["Id asc"],
                ...(query ?? {}),
                $filter: [`LoteNotificacionId eq ${loteNotificacionId}`, ...(query?.$filter ?? [])],
            }),

        // Ver el comentario junto a ResumenLoteImpresion: endpoint nuevo, pendiente de implementar
        // en el backend.
        obtenerResumenLotesImpresion: async (filtro: ResumenLotesFiltro): Promise<ActionResult<ResumenLoteImpresion[]>> => {
            try {
                const params = new URLSearchParams();
                if (filtro.fechaEnvioDesde) params.set("fechaEnvioDesde", filtro.fechaEnvioDesde);
                if (filtro.fechaEnvioHasta) params.set("fechaEnvioHasta", filtro.fechaEnvioHasta);
                if (filtro.configuracionProduccionId != null) params.set("configuracionProduccionId", String(filtro.configuracionProduccionId));
                const response = await notificacionApi.axiosInstance.get<any>(`Notificacion/email/lote-impresion/resumen?${params.toString()}`);
                const body = response.data ?? [];
                const items: any[] = Array.isArray(body) ? body : (pick<any[]>(body, "value", "Value", []) ?? []);
                return { data: { value: items.map(mapResumenLoteImpresion) } };
            } catch (error: any | AxiosError) {
                return { error };
            }
        },
    };
};
